import React, { useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaSearch,
  FaUsers,
  FaChalkboardTeacher,
} from "react-icons/fa";

function AvatarImage({ src, alt }) {
  return (
    <img
      src={src || "/img/no-image.jpg"}
      alt={alt}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = "/img/no-image.jpg";
      }}
      className="h-14 w-14 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 bg-white"
    />
  );
}

function Pill({ icon: Icon, children, tone = "slate" }) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    violet:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    amber:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    rose:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${tones[tone] || tones.slate}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

function AdherentPill({ adherent }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 px-3 py-2 text-sm">
      <div className="font-medium text-slate-900 dark:text-white">
        {adherent?.prenom} {adherent?.nom}
      </div>
    </div>
  );
}

const ClubDetailPanel = ({
  club,
  adherentsByGroup,
  loadingAdherents,
  onLoadAdherents,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDojoId, setSelectedDojoId] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);

  const dojoOptions = useMemo(() => {
    return club.dojosDetailed || [];
  }, [club]);

  const visibleDojos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const mapped = (club.dojosDetailed || []).map((dojo) => {
      let visibleGroups = dojo.groups || [];

      if (term) {
        visibleGroups = visibleGroups.filter((group) => {
          const teachersText = (group.enseignants || [])
            .map((t) => `${t.prenom || ""} ${t.nom || ""}`)
            .join(" ")
            .toLowerCase();

          const text = `
            ${group.nom_groupe || ""}
            ${teachersText}
            ${dojo.nom_dojo || ""}
          `.toLowerCase();

          return text.includes(term);
        });
      }

      visibleGroups = [...visibleGroups].sort((a, b) => {
        switch (sortBy) {
          case "adherents_desc":
            return Number(b.adherentsCount || 0) - Number(a.adherentsCount || 0);
          case "teachers_desc":
            return (b.enseignants?.length || 0) - (a.enseignants?.length || 0);
          case "name":
          default:
            return String(a.nom_groupe || "").localeCompare(
              String(b.nom_groupe || ""),
              "fr"
            );
        }
      });

      return {
        ...dojo,
        visibleGroups,
      };
    });

    return mapped.filter((dojo) => {
      const matchesDojo = selectedDojoId
        ? String(dojo.id) === String(selectedDojoId)
        : true;

      if (!matchesDojo) return false;

      if (!searchTerm.trim()) return true;

      const dojoMatches = String(dojo.nom_dojo || "")
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());

      return dojoMatches || dojo.visibleGroups.length > 0;
    });
  }, [club, searchTerm, selectedDojoId, sortBy]);

  const visibleGroupIds = useMemo(() => {
    return visibleDojos.flatMap((dojo) => dojo.visibleGroups.map((group) => group.id));
  }, [visibleDojos]);

  const areAllVisibleGroupsExpanded =
    visibleGroupIds.length > 0 &&
    visibleGroupIds.every((id) => expandedGroupIds.includes(id));

  const toggleGroup = async (groupId) => {
    const isOpen = expandedGroupIds.includes(groupId);

    if (isOpen) {
      setExpandedGroupIds((prev) => prev.filter((id) => id !== groupId));
      return;
    }

    setExpandedGroupIds((prev) => [...prev, groupId]);
    await onLoadAdherents(groupId);
  };

  const toggleAllVisibleGroups = async () => {
    if (areAllVisibleGroupsExpanded) {
      setExpandedGroupIds((prev) =>
        prev.filter((id) => !visibleGroupIds.includes(id))
      );
      return;
    }

    setExpandedGroupIds((prev) => [
      ...new Set([...prev, ...visibleGroupIds]),
    ]);

    await Promise.all(visibleGroupIds.map((groupId) => onLoadAdherents(groupId)));
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
        <div className="flex flex-col xl:flex-row gap-4 xl:items-end xl:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              {club.nom_club}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Explore les dojos, groupes, enseignants et adhérents du club.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Pill icon={FaMapMarkerAlt} tone="emerald">
              {club.totalDojos || 0} dojo{club.totalDojos > 1 ? "s" : ""}
            </Pill>
            <Pill icon={FaLayerGroup} tone="violet">
              {club.totalGroups || 0} groupe{club.totalGroups > 1 ? "s" : ""}
            </Pill>
            <Pill icon={FaUsers} tone="amber">
              {club.totalAdherents || 0} adhérent{club.totalAdherents > 1 ? "s" : ""}
            </Pill>
            <Pill icon={FaChalkboardTeacher} tone="rose">
              {club.teachersCount || 0} enseignant{club.teachersCount > 1 ? "s" : ""}
            </Pill>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Recherche groupe / dojo / enseignant
            </label>
            <div className="mt-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nom du groupe, dojo, enseignant..."
                className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-10 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Filtrer par dojo
            </label>
            <select
              value={selectedDojoId}
              onChange={(e) => setSelectedDojoId(e.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
            >
              <option value="">Tous les dojos</option>
              {dojoOptions.map((dojo) => (
                <option key={dojo.id} value={dojo.id}>
                  {dojo.nom_dojo}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Trier les groupes
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-500/30"
            >
              <option value="name">Nom</option>
              <option value="adherents_desc">Nb adhérents</option>
              <option value="teachers_desc">Nb enseignants</option>
            </select>
          </div>

          <div className="lg:col-span-2 flex items-end">
            <button
              onClick={toggleAllVisibleGroups}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition"
            >
              {areAllVisibleGroupsExpanded ? <FaChevronUp /> : <FaChevronDown />}
              {areAllVisibleGroupsExpanded ? "Tout replier" : "Tout ouvrir"}
            </button>
          </div>
        </div>
      </div>

      {visibleDojos.length === 0 ? (
        <div className="rounded-[28px] border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Aucun résultat
          </h3>
          <p className="text-sm text-slate-500 mt-2">
            Essaie de modifier les filtres de recherche.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleDojos.map((dojo) => (
            <div
              key={dojo.id}
              className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
            >
              <div className="p-5 md:p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-start gap-4">
                  <AvatarImage src={dojo.logo_dojo} alt={dojo.nom_dojo} />

                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {dojo.nom_dojo || "Dojo sans nom"}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {dojo.adresse_dojo || "Adresse non renseignée"}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Pill icon={FaLayerGroup} tone="violet">
                        {dojo.totalGroups || 0} groupe{dojo.totalGroups > 1 ? "s" : ""}
                      </Pill>
                      <Pill icon={FaUsers} tone="amber">
                        {dojo.totalAdherents || 0} adhérent
                        {dojo.totalAdherents > 1 ? "s" : ""}
                      </Pill>
                      <Pill icon={FaChalkboardTeacher} tone="rose">
                        {dojo.teachersCount || 0} enseignant
                        {dojo.teachersCount > 1 ? "s" : ""}
                      </Pill>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6 space-y-3 bg-slate-50/60 dark:bg-slate-950/20">
                {dojo.visibleGroups.length > 0 ? (
                  dojo.visibleGroups.map((group) => {
                    const isExpanded = expandedGroupIds.includes(group.id);
                    const adherents = adherentsByGroup[group.id] || [];
                    const isLoadingAdherents = loadingAdherents[group.id];

                    return (
                      <div
                        key={group.id}
                        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
                      >
                        <div className="p-4">
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                            <div>
                              <h4 className="text-base font-semibold text-slate-900 dark:text-white">
                                {group.nom_groupe}
                              </h4>

                              <div className="mt-3 flex flex-wrap gap-2">
                                <Pill icon={FaUsers} tone="amber">
                                  {group.adherentsCount || 0} adhérent
                                  {group.adherentsCount > 1 ? "s" : ""}
                                </Pill>
                                <Pill icon={FaChalkboardTeacher} tone="rose">
                                  {group.enseignants?.length || 0} enseignant
                                  {group.enseignants?.length > 1 ? "s" : ""}
                                </Pill>
                              </div>

                              <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                                {group.enseignants?.length > 0 ? (
                                  <span>
                                    <span className="font-medium">Enseignants :</span>{" "}
                                    {group.enseignants
                                      .map((e) => `${e.prenom || ""} ${e.nom || ""}`.trim())
                                      .join(", ")}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">Aucun enseignant</span>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => toggleGroup(group.id)}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 bg-sky-600 text-white hover:bg-sky-700 transition"
                            >
                              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                              {isExpanded ? "Masquer" : "Voir les adhérents"}
                            </button>
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/30 p-4">
                            {isLoadingAdherents ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                                {Array.from({ length: 3 }).map((_, i) => (
                                  <div
                                    key={i}
                                    className="h-12 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse"
                                  />
                                ))}
                              </div>
                            ) : adherents.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                                {adherents.map((adherent) => (
                                  <AdherentPill key={adherent.id} adherent={adherent} />
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500 italic">
                                Aucun adhérent dans ce groupe.
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900">
                    Aucun groupe visible pour ce dojo.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClubDetailPanel;